import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, ShoppingBag, Sun, Thermometer, Waves } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useCart } from "@/context/CartContext";
import { api, eur, img } from "@/lib/api";
import { DopingList, useDopingInfo } from "@/pages/Antidoping";

const ALARMS = [["colico", "Dolor de cólico (se tumba, se mira el flanco, patea)"], ["no_come", "No come"], ["no_defeca", "No defeca"], ["cojera", "Cojera intensa"], ["fiebre", "Fiebre"], ["herida", "Herida profunda"]];

const SEASONS = [
  { icon: Sun, t: "Llega el verano", d: "Equi Care y Oil All In One para la temporada de picores y heridas.", slugs: ["equi-care", "oil-all-in-one"] },
  { icon: Thermometer, t: "Días de calor", d: "Isotonic para reponer los electrolitos que pierde con el sudor.", slugs: ["isotonic"] },
  { icon: Waves, t: "Tandas periódicas", d: "Sand Free varios días seguidos cada mes para un digestivo limpio.", slugs: ["sand-free"] },
];

const ProductReco = ({ p }) => {
  const { add } = useCart();
  const [fmt, setFmt] = useState(p.formats[0]);
  return (
    <div className="flex gap-4 bg-white border border-slate-200 rounded p-4" data-testid={`guide-product-${p.slug}`}>
      <img src={img(p.images[0])} alt={p.name} className="w-20 h-20 object-contain bg-[#EFF6FF] rounded" />
      <div className="flex-1 min-w-0">
        <Link to={`/producto/${p.slug}`} className="font-display font-extrabold text-xl text-[#0B1D3A] hover:text-[#DC2626]">{p.name}</Link>
        <p className="text-sm"><b className="text-[#1E3A8A]">Cómo usarlo:</b> {p.dose}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          {p.formats.map((f) => <button key={f.id} onClick={() => setFmt(f)} className={`text-xs px-2.5 py-1 rounded-full border ${fmt.id === f.id ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300"}`}>{f.label}</button>)}
          <span className="font-bold text-[#DC2626] ml-1">{eur(fmt.price)}</span>
        </div>
      </div>
      <button onClick={() => add(p, fmt)} data-testid={`guide-add-${p.slug}`} aria-label="Añadir" className="self-center bg-[#DC2626] hover:bg-[#B91C1C] text-white p-3 rounded-full"><ShoppingBag size={17} /></button>
    </div>
  );
};

export default function HealthGuide() {
  const [guide, setGuide] = useState([]);
  const [products, setProducts] = useState({});
  const [sel, setSel] = useState(null);
  const [alarms, setAlarms] = useState([]);
  const doping = useDopingInfo();

  useEffect(() => {
    api.get("/guide").then((r) => setGuide(r.data));
    api.get("/products").then((r) => setProducts(Object.fromEntries(r.data.map((p) => [p.slug, p]))));
  }, []);

  const situation = guide.find((g) => g.key === sel);
  const alarm = alarms.length > 0;
  const toggle = (k) => setAlarms((a) => (a.includes(k) ? a.filter((x) => x !== k) : [...a, k]));

  return (
    <div data-testid="guide-page">
      <section className="bg-[#0B1D3A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-14">
          <p className="text-[#60A5FA] font-bold text-sm tracking-[0.2em] uppercase">Gratis · Sin cuenta</p>
          <h1 className="font-display font-black text-5xl sm:text-6xl mt-2">Guía de salud y prevención</h1>
          <p className="text-blue-100 mt-4 max-w-2xl">Elige qué le pasa a tu caballo o qué quieres conseguir, y te decimos qué productos FAUNNY le ayudan y cómo usarlos.</p>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="border-2 border-[#DC2626]/30 rounded p-5 bg-white" data-testid="guide-alarm-box">
          <p className="font-display font-extrabold text-xl text-[#0B1D3A]">Antes de empezar: ¿presenta alguno de estos síntomas?</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {ALARMS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm cursor-pointer">
                <Checkbox checked={alarms.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`guide-alarm-${k}`} /> {l}
              </label>
            ))}
          </div>
        </div>

        {alarm ? (
          <div className="mt-8 bg-[#DC2626] text-white rounded p-8 rise" data-testid="guide-vet-warning">
            <PhoneCall size={36} />
            <h2 className="font-display font-black text-4xl mt-3">Llama al veterinario de inmediato</h2>
            <p className="mt-3 max-w-2xl text-red-50">Los síntomas que has marcado pueden indicar un problema grave que necesita atención veterinaria urgente. En este caso no recomendamos ningún producto: lo primero es que un veterinario examine a tu caballo.</p>
          </div>
        ) : (
          <>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B1D3A] mt-12">¿Qué le pasa o qué quieres conseguir?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
              {guide.map((g) => (
                <button key={g.key} onClick={() => setSel(g.key)} data-testid={`guide-situation-${g.key}`}
                  className={`text-left p-5 rounded border-2 font-display font-bold text-lg leading-tight transition-colors ${sel === g.key ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "border-slate-200 text-[#0B1D3A] hover:border-[#2563EB]"}`}>
                  {g.title}
                </button>
              ))}
            </div>
            {situation && (
              <div className="mt-8 bg-[#EFF6FF] rounded p-6 rise" data-testid="guide-result">
                <h3 className="font-display font-black text-3xl text-[#0B1D3A]">{situation.title}</h3>
                <p className="text-slate-700 mt-2 max-w-3xl" data-testid="guide-reason">{situation.reason}</p>
                <div className="grid lg:grid-cols-2 gap-3 mt-5">
                  {situation.products.map((s) => products[s] && <ProductReco key={s} p={products[s]} />)}
                </div>
              </div>
            )}
            <h2 className="font-display font-black text-3xl sm:text-4xl text-[#0B1D3A] mt-16">Prevención por temporada</h2>
            <div className="grid md:grid-cols-3 gap-4 mt-6">
              {SEASONS.map(({ icon: Icon, t, d, slugs }) => (
                <div key={t} className="border border-slate-200 rounded p-6">
                  <Icon className="text-[#DC2626]" size={28} />
                  <h3 className="font-display font-extrabold text-2xl text-[#0B1D3A] mt-3">{t}</h3>
                  <p className="text-sm text-slate-600 mt-2">{d}</p>
                  <div className="flex flex-wrap gap-2 mt-4">{slugs.map((s) => <Link key={s} to={`/producto/${s}`} className="text-xs font-bold bg-[#0B1D3A] text-white px-3 py-1.5 rounded-full">{products[s]?.name.replace("FAUNNY ", "") || s}</Link>)}</div>
                </div>
              ))}
            </div>
          </>
        )}
        {doping && (
          <section className="mt-12 border-2 border-[#0B1D3A] rounded p-5 sm:p-7" data-testid="guide-doping">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#DC2626]">Competición</p>
            <h2 className="font-display font-black text-3xl text-[#0B1D3A] mt-1">Antidopaje FEI: ingredientes que pueden dar positivo</h2>
            <p className="text-sm text-slate-600 mt-2 mb-5">{doping.notice}</p>
            <DopingList info={doping} compact />
            <Link to="/antidopaje" className="inline-block mt-5 font-display font-bold text-[#DC2626]" data-testid="guide-doping-link">Ver la lista completa y revisar una fórmula →</Link>
          </section>
        )}
        <p className="text-xs text-slate-500 mt-10">Esta guía es orientativa. Nuestros productos son piensos complementarios y no sustituyen el diagnóstico ni el tratamiento veterinario.</p>
      </div>
    </div>
  );
}
